import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from './api/client';
import { decode, decodeBlob } from './audio/context';
import * as edit from './audio/edit';
import { Player } from './audio/player';
import { Recorder, isRecordingSupported, micErrorMessage, requestWakeLock } from './audio/recorder';
import { bufferToWav, download, mixTracksToWav } from './audio/wav';
import TopBar from './components/TopBar';
import LoopList from './components/LoopList';
import Tracks from './components/Tracks';
import SidePanel, { type PanelHandlers } from './components/SidePanel';
import Transport from './components/Transport';
import Waveform from './components/Waveform';
import { BusyOverlay, EmptyOverlay, RecordingOverlay } from './components/Overlays';
import AccountModal from './components/AccountModal';
import LibraryModal from './components/LibraryModal';
import { useAccount, accountsEnabled } from './lib/supabase';
import { saveTake, type TakeRow } from './lib/library';
import { track } from './lib/track';
import { words } from './copy';
import { clamp } from './format';
import { useSession, type State } from './state/session';
import type { Loop, Mode, Selection, Track } from './types';

export default function App({ onOpenStudio }: { onOpenStudio?: () => void } = {}) {
  const [s, dispatch] = useSession();
  const player = useMemo(() => new Player(), []);
  const recorder = useRef<Recorder | null>(null);
  const wakeLock = useRef<{ release(): Promise<void> } | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const taps = useRef<number[]>([]);

  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [recElapsed, setRecElapsed] = useState(0);

  /* ------------------------------------------------------------- comptes */
  const user = useAccount();
  const [accountOpen, setAccountOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [cloudBusy, setCloudBusy] = useState(false);
  const [recLevel, setRecLevel] = useState(0);
  const [mixMode, setMixMode] = useState(false);      // lecture « toutes les pistes »
  const [soloTrack, setSoloTrack] = useState<string | null>(null);

  const w = useMemo(() => words(s.mode), [s.mode]);
  const patch = useCallback((p: Partial<State>) => dispatch({ type: 'patch', patch: p }), [dispatch]);
  const fail = useCallback((e: unknown) => {
    const msg = e instanceof Error ? e.message : 'Une erreur est survenue.';
    dispatch({ type: 'patch', patch: { error: msg, busy: null } });
  }, [dispatch]);

  /** Buffer réellement audible (bascule Avant / Après). */
  const heard = s.showOriginal && s.preQuant ? s.preQuant : s.buffer;
  const duration = heard?.duration ?? 0;
  const hasAudio = !!heard && s.status !== 'recording';

  /* ---------------------------------------------------- serveur */
  const wasOffline = useRef(false);
  const checkHealth = useCallback(async () => {
    try {
      const h = await api.health();
      wasOffline.current = false;
      dispatch({ type: 'patch', patch: { health: h, serverOk: true } });
    } catch {
      if (!wasOffline.current) {
        wasOffline.current = true; // un seul événement par passage hors ligne
        track('serveur-indisponible');
      }
      dispatch({ type: 'patch', patch: { serverOk: false } });
    }
  }, [dispatch]);

  useEffect(() => {
    checkHealth();
    const id = window.setInterval(checkHealth, 30000);
    return () => window.clearInterval(id);
  }, [checkHealth]);

  /* ---------------------------------------------------- analyse */
  const runAnalyze = useCallback(
    async (buffer?: AudioBuffer) => {
      const buf = buffer ?? s.buffer;
      if (!buf) return;
      dispatch({ type: 'patch', patch: { busy: 'Analyse du tempo…', error: null } });
      try {
        const analysis = await api.analyze(bufferToWav(buf));
        dispatch({ type: 'analysis', analysis });
        dispatch({ type: 'patch', patch: { busy: null, serverOk: true } });
      } catch (e) {
        // L'analyse est une commodité : pas d'erreur bloquante si le serveur manque.
        dispatch({
          type: 'patch',
          patch: { busy: null, serverOk: e instanceof api.ApiError },
        });
      }
    },
    [s.buffer, dispatch],
  );

  /* ---------------------------------------------------- lecture */
  useEffect(() => {
    player.onChange = () => setPlaying(player.isPlaying);
    let raf = 0;
    const tick = () => {
      setPlayhead(player.time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      player.stop();
    };
  }, [player]);

  const playRange = useCallback(
    (region?: Selection, loop = false) => {
      if (!heard) return;
      player.play(heard, region, loop).catch(fail);
    },
    [heard, player, fail],
  );

  const onPlay = useCallback(() => {
    if (!heard) return;
    setMixMode(false);
    setSoloTrack(null);
    const sel = s.selection && s.selection.end - s.selection.start > 0.05 ? s.selection : undefined;
    playRange(sel ?? { start: playhead, end: heard.duration });
  }, [heard, s.selection, playhead, playRange]);

  const onPlayLoops = useCallback(() => {
    if (!heard) return;
    setMixMode(false);
    setSoloTrack(null);
    const regions = s.loops.filter((l) => l.mixed).map((l) => ({ start: l.start, end: l.end }));
    if (!regions.length) return;
    player.playRegions(heard, regions).catch(fail);
  }, [heard, s.loops, player, fail]);

  /* ---------------------------------------------------- pistes (couches) */
  const nextTrackName = useCallback(() => `Piste ${s.tracks.length + 1}`, [s.tracks.length]);

  const onAddTake = useCallback(() => {
    if (!s.buffer) return;
    dispatch({ type: 'track/add', buffer: s.buffer, name: nextTrackName() });
  }, [s.buffer, dispatch, nextTrackName]);

  const activeLoopObj = s.loops.find((l) => l.id === s.activeLoop) ?? null;

  const onAddLoop = useCallback(() => {
    if (!s.buffer || !activeLoopObj) return;
    const buf = edit.slice(s.buffer, activeLoopObj.start, activeLoopObj.end);
    dispatch({ type: 'track/add', buffer: buf, name: activeLoopObj.name });
  }, [s.buffer, activeLoopObj, dispatch]);

  const onPlayTrack = useCallback(
    (t: Track) => {
      setMixMode(false);
      setSoloTrack(t.id);
      player.play(t.buffer, undefined, true).catch(fail);
    },
    [player, fail],
  );

  const onPlayAllTracks = useCallback(() => {
    const items = s.tracks.filter((t) => !t.muted).map((t) => ({ buffer: t.buffer, volume: t.volume }));
    if (!items.length) return;
    setSoloTrack(null);
    setMixMode(true);
    player.playBuffers(items, true).catch(fail);
  }, [s.tracks, player, fail]);

  const onExportMix = useCallback(() => {
    const items = s.tracks.filter((t) => !t.muted).map((t) => ({ buffer: t.buffer, volume: t.volume }));
    if (!items.length) return;
    const base = (s.name || 'quantime').replace(/\.[^.]+$/, '');
    download(mixTracksToWav(items, items[0].buffer.sampleRate), `${base}-mix.wav`);
  }, [s.tracks, s.name]);

  /* ---------------------------------------------------- enregistrement */
  const startRecording = useCallback(async () => {
    player.stop();
    const rec = new Recorder();
    try {
      await rec.start();
    } catch (e) {
      fail(new Error(micErrorMessage(e)));
      rec.dispose();
      return;
    }
    recorder.current = rec;
    wakeLock.current = await requestWakeLock();
    setRecElapsed(0);
    patch({ status: 'recording', error: null });
  }, [player, fail, patch]);

  useEffect(() => {
    if (s.status !== 'recording') return;
    const id = window.setInterval(() => {
      const rec = recorder.current;
      if (!rec) return;
      setRecElapsed(rec.elapsed());
      setRecLevel(rec.level());
    }, 80);
    return () => window.clearInterval(id);
  }, [s.status]);

  const stopRecording = useCallback(async () => {
    const rec = recorder.current;
    if (!rec) return;
    patch({ busy: 'Préparation de la prise…' });
    try {
      const blob = await rec.stop();
      recorder.current = null;
      wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
      const buffer = await decodeBlob(blob);
      track('enregistrement-termine', { contexte: 'editeur', duree_sec: Math.round(buffer.duration) });
      const name = `prise-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}`;
      dispatch({ type: 'loaded', buffer, name });
      void runAnalyze(buffer);
    } catch (e) {
      fail(e);
      patch({ status: 'empty' });
    }
  }, [dispatch, fail, patch, runAnalyze]);

  /* ---------------------------------------------------- import */
  const onImport = useCallback(() => fileInput.current?.click(), []);

  const onFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      player.stop();
      patch({ busy: 'Lecture du fichier…', error: null });
      try {
        const buffer = await decodeBlob(file);
        track('chargement-fichier', { format: file.name.split('.').pop()?.toLowerCase() ?? '?', contexte: 'editeur' });
        dispatch({ type: 'loaded', buffer, name: file.name });
        void runAnalyze(buffer);
      } catch {
        fail(new Error('Ce fichier n’a pas pu être décodé par ce navigateur. Essaie un WAV ou un MP3.'));
      }
    },
    [dispatch, fail, patch, player, runAnalyze],
  );

  /* ---------------------------------------------------- appels serveur */
  const runQuantize = useCallback(async () => {
    if (!s.buffer) return;
    const bpm = s.bpm || s.analysis?.bpm || 0;
    if (!bpm) {
      fail(new Error('Tempo inconnu : lance « Détecter » ou saisis le BPM.'));
      return;
    }
    player.stop();
    patch({ busy: 'On remet tout en rythme…', error: null });
    try {
      const sel = s.selectionOnly && s.selection ? s.selection : null;
      const { wav, meta } = await api.quantize(bufferToWav(s.buffer), {
        bpm,
        division: s.division,
        strength: s.strength,
        mode: s.quantMode,
        selection: sel,
      });
      const buffer = await decode(wav);
      dispatch({ type: 'replace', buffer, quantized: true, meta });
      void runAnalyze(buffer);
    } catch (e) {
      fail(e);
    }
  }, [s.buffer, s.bpm, s.analysis, s.division, s.strength, s.quantMode, s.selectionOnly, s.selection, player, dispatch, patch, fail, runAnalyze]);

  const runDenoise = useCallback(async () => {
    if (!s.buffer) return;
    player.stop();
    patch({ busy: 'Nettoyage du son…', error: null });
    try {
      const { wav } = await api.denoise(bufferToWav(s.buffer), {
        amount: s.nrAmount,
        useHighpass: s.useHighpass,
        useHum: s.useHum,
        profile: s.selection && s.selection.end - s.selection.start > 0.1 ? s.selection : null,
      });
      const buffer = await decode(wav);
      dispatch({ type: 'replace', buffer });
    } catch (e) {
      fail(e);
    }
  }, [s.buffer, s.nrAmount, s.useHighpass, s.useHum, s.selection, player, dispatch, patch, fail]);

  /* ---------------------------------------------------- éditions locales */
  const localEdit = useCallback(
    (fn: (b: AudioBuffer) => AudioBuffer) => {
      if (!s.buffer) return;
      player.stop();
      try {
        dispatch({ type: 'replace', buffer: fn(s.buffer) });
      } catch (e) {
        fail(e);
      }
    },
    [s.buffer, player, dispatch, fail],
  );

  const onExport = useCallback(() => {
    if (!heard) return;
    const base = (s.name || 'quantime').replace(/\.[^.]+$/, '');
    download(bufferToWav(heard), `${base}${s.quantized && !s.showOriginal ? '-cale' : ''}.wav`);
  }, [heard, s.name, s.quantized, s.showOriginal]);

  const onExportLoop = useCallback(
    (l: Loop) => {
      if (!heard) return;
      const base = (s.name || 'quantime').replace(/\.[^.]+$/, '');
      download(bufferToWav(heard, l.start, l.end), `${base}-${l.name.toLowerCase().replace(/\s+/g, '')}.wav`);
    },
    [heard, s.name],
  );

  /* ---------------------------------------------------- tempo */
  const setBpm = useCallback((v: number) => patch({ bpm: clamp(v, 30, 300) }), [patch]);

  const onTap = useCallback(() => {
    const now = performance.now();
    taps.current = [...taps.current.filter((t) => now - t < 3000), now];
    if (taps.current.length < 3) return;
    const gaps: number[] = [];
    for (let i = 1; i < taps.current.length; i++) gaps.push(taps.current[i] - taps.current[i - 1]);
    const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    if (mean > 0) setBpm(Math.round((60000 / mean) * 10) / 10);
  }, [setBpm]);

  const gridOffset = s.quantMeta?.grid_offset ?? 0;

  const snapSelection = useCallback(
    (edge: 'start' | 'end') => {
      const bpm = s.bpm || s.analysis?.bpm || 0;
      if (!bpm || !s.selection) return;
      const step = (60 / bpm) * s.division;
      const snap = (t: number) => Math.round((t - gridOffset) / step) * step + gridOffset;
      const next = { ...s.selection };
      next[edge] = clamp(snap(next[edge]), 0, duration);
      if (next.end - next.start > 0.02) patch({ selection: next });
    },
    [s.bpm, s.analysis, s.selection, s.division, gridOffset, duration, patch],
  );

  /* ---------------------------------------------------- zoom */
  const zoom = useCallback(
    (factor: number) => {
      if (!heard) return;
      const view = s.view.end > s.view.start ? s.view : { start: 0, end: heard.duration };
      const span = view.end - view.start;
      const centre = playhead >= view.start && playhead <= view.end ? playhead : view.start + span / 2;
      const next = clamp(span * factor, 0.2, heard.duration);
      let start = centre - next / 2;
      let end = centre + next / 2;
      if (start < 0) { end -= start; start = 0; }
      if (end > heard.duration) { start -= end - heard.duration; end = heard.duration; }
      patch({ view: { start: Math.max(0, start), end } });
    },
    [heard, s.view, playhead, patch],
  );

  const zoomFit = useCallback(() => {
    if (heard) patch({ view: { start: 0, end: heard.duration } });
  }, [heard, patch]);

  const zoomSelection = useCallback(() => {
    if (s.selection && s.selection.end - s.selection.start > 0.1) patch({ view: { ...s.selection } });
    else zoomFit();
  }, [s.selection, patch, zoomFit]);

  /* ---------------------------------------------------- raccourcis clavier */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (player.isPlaying) player.stop();
        else onPlay();
      } else if (e.key === 'z' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        dispatch({ type: 'undo' });
      } else if (e.key === 'Escape') {
        patch({ selection: null });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [player, onPlay, dispatch, patch]);

  /* ---------------------------------------------------- panneau */
  const handlers: PanelHandlers = {
    patch,
    onQuantize: runQuantize,
    onDenoise: runDenoise,
    onExport,
    onRepair: () => s.selection && localEdit((b) => edit.repair(b, s.selection!.start, s.selection!.end)),
    onCut: () => s.selection && localEdit((b) => edit.cut(b, s.selection!.start, s.selection!.end)),
    onNormalize: () => localEdit((b) => edit.normalize(b)),
    onUndo: () => dispatch({ type: 'undo' }),
    onDetect: () => void runAnalyze(),
    onHalve: () => setBpm((s.bpm || s.analysis?.bpm || 120) / 2),
    onDouble: () => setBpm((s.bpm || s.analysis?.bpm || 120) * 2),
    onTap,
    onSnapSelection: snapSelection,
    onMode: (m: Mode) => dispatch({ type: 'mode', mode: m }),
    onRetryServer: checkHealth,
  };

  const hint =
    s.status === 'empty'
      ? 'Enregistre ou importe pour commencer.'
      : s.status === 'recording'
        ? 'Joue — on capte.'
        : s.busy
          ? 'Traitement en cours…'
          : s.serverOk === false
            ? 'Fonctions serveur indisponibles.'
            : s.quantized
              ? 'Compare avant / après avant d’exporter.'
              : s.mode === 'troubadour'
                ? w.tip
                : w.tipPro;

  const legendOnsets = s.quantized ? w.onsetsDone : w.onsets;

  return (
    <div className="app">
      <TopBar
        mode={s.mode}
        status={s.status}
        name={s.name}
        duration={duration}
        analysis={s.analysis}
        quantized={s.quantized}
        quantMeta={s.quantMeta}
        recElapsed={recElapsed}
        serverOk={s.serverOk}
        onMode={(m) => dispatch({ type: 'mode', mode: m })}
        onOpenStudio={onOpenStudio}
        accountEnabled={accountsEnabled()}
        accountEmail={user?.email ?? null}
        onAccount={() => setAccountOpen(true)}
        onCloudSave={async () => {
          if (!user || !s.buffer) return;
          setCloudBusy(true);
          try {
            await saveTake(user.id, s.name || 'prise', s.buffer, {
              bpm: s.analysis?.bpm ?? (s.bpm || null),
              keyName: s.analysis?.key?.name ?? null,
            });
            patch({ busy: 'Sauvegardé dans ta bibliothèque ✓' });
            window.setTimeout(() => patch({ busy: null }), 1400);
          } catch (e) {
            fail(e instanceof Error ? e : new Error('Sauvegarde impossible.'));
          } finally {
            setCloudBusy(false);
          }
        }}
        cloudBusy={cloudBusy}
      />

      {accountOpen && (
        <AccountModal
          user={user}
          onClose={() => setAccountOpen(false)}
          onOpenLibrary={() => { setAccountOpen(false); setLibOpen(true); }}
        />
      )}
      {libOpen && user && (
        <LibraryModal
          user={user}
          context="editor"
          onClose={() => setLibOpen(false)}
          onLoadTake={(buffer, row: TakeRow) => {
            player.stop();
            dispatch({ type: 'loaded', buffer, name: row.name });
            void runAnalyze(buffer);
          }}
        />
      )}

      <div className="body">
        <div className="main">
          <div className={`stage${s.status === 'recording' ? ' armed' : ''}`}>
            <Waveform
              buffer={s.status === 'recording' ? null : heard}
              view={s.view.end > s.view.start ? s.view : { start: 0, end: duration }}
              selection={s.selection}
              loops={s.loops}
              activeLoop={s.activeLoop}
              onsets={s.analysis?.onsets ?? []}
              bpm={s.bpm || s.analysis?.bpm || null}
              division={s.division}
              gridOffset={gridOffset}
              quantized={s.quantized && !s.showOriginal}
              mode={s.mode}
              player={player}
              interactive={hasAudio && !s.busy}
              onSelect={(sel) => patch({ selection: sel })}
              onSeek={(t) => {
                player.stop();
                player.seek(t);
                setPlayhead(t);
              }}
              onView={(v) => patch({ view: v })}
            />

            {hasAudio && (
              <>
                <div className="legend">
                  <span>
                    <i style={{ color: s.quantized ? 'var(--green)' : 'var(--orange)' }}>▮</i> {legendOnsets}
                  </span>
                  <span>
                    <i style={{ color: '#7986cb' }}>▮</i> {w.grid}
                  </span>
                  {s.loops.length > 0 && (
                    <span>
                      <i style={{ color: 'var(--violet)' }}>▮</i> {w.loopsLower}
                    </span>
                  )}
                </div>
                <div className="zoom">
                  <button onClick={() => zoom(0.5)} aria-label="Zoomer">＋</button>
                  <button onClick={() => zoom(2)} aria-label="Dézoomer">−</button>
                  <button onClick={zoomSelection} aria-label="Ajuster à la sélection">⤢</button>
                </div>
              </>
            )}

            {s.status === 'empty' && !s.busy && (
              <EmptyOverlay
                onRecord={startRecording}
                onImport={onImport}
                canRecord={isRecordingSupported()}
              />
            )}
            {s.status === 'recording' && (
              <RecordingOverlay elapsed={recElapsed} level={recLevel} onStop={stopRecording} />
            )}
            {s.busy && <BusyOverlay label={s.busy} />}
          </div>

          {hasAudio && (
            <LoopList
              loops={s.loops}
              activeLoop={s.activeLoop}
              duration={duration}
              mode={s.mode}
              w={w}
              canCreate={!!s.selection && s.selection.end - s.selection.start > 0.05}
              onCreate={() =>
                s.selection && (track('boucle-creee'), dispatch({ type: 'loop/add', start: s.selection.start, end: s.selection.end }))
              }
              onPlay={(l) => {
                dispatch({ type: 'loop/active', id: l.id });
                playRange({ start: l.start, end: l.end }, true);
              }}
              onSelect={(id) => dispatch({ type: 'loop/active', id })}
              onMix={(id, mixed) => dispatch({ type: 'loop/mix', id, mixed })}
              onRemove={(id) => dispatch({ type: 'loop/remove', id })}
              onMove={(id, edge, delta) => dispatch({ type: 'loop/move', id, edge, delta })}
              onExport={onExportLoop}
            />
          )}

          {(hasAudio || s.tracks.length > 0) && (
            <Tracks
              tracks={s.tracks}
              w={w}
              canAddTake={!!s.buffer}
              canAddLoop={!!s.buffer && !!activeLoopObj}
              playingAll={playing && mixMode}
              playingTrack={playing ? soloTrack : null}
              onAddTake={onAddTake}
              onAddLoop={onAddLoop}
              onPlayAll={onPlayAllTracks}
              onStop={() => {
                player.stop();
                setMixMode(false);
                setSoloTrack(null);
              }}
              onPlay={onPlayTrack}
              onVolume={(id, v) => dispatch({ type: 'track/patch', id, patch: { volume: v } })}
              onMute={(id, muted) => dispatch({ type: 'track/patch', id, patch: { muted } })}
              onRemove={(id) => dispatch({ type: 'track/remove', id })}
              onExportMix={onExportMix}
            />
          )}
        </div>

        <aside className="side">
          <SidePanel s={s} w={w} h={handlers} busy={!!s.busy} />
        </aside>
      </div>

      <Transport
        status={s.status}
        hasAudio={hasAudio}
        isPlaying={playing}
        playhead={playhead}
        duration={duration}
        recElapsed={recElapsed}
        quantized={s.quantized}
        showOriginal={s.showOriginal}
        hasLoops={s.loops.some((l) => l.mixed)}
        hint={hint}
        w={w}
        onRecord={startRecording}
        onStopRecord={stopRecording}
        onImport={onImport}
        onPlay={onPlay}
        onStop={() => player.stop()}
        onPlayLoops={onPlayLoops}
        onCompare={(original) => {
          player.stop();
          patch({ showOriginal: original });
        }}
      />

      {s.error && (
        <div className="toast" role="alert">
          <span>{s.error}</span>
          <button className="btn xs ghost" onClick={() => patch({ error: null })} aria-label="Fermer">
            ✕
          </button>
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="audio/*,.wav,.mp3,.m4a,.aac,.flac,.ogg,.opus,.webm"
        className="sr"
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
